import { getMillisSinceLastActivity } from 'globals/utils';
import Socket from './socket';
import Poller from './poller';
import * as socketConstants from './socket/constants';

const ONE_MINUTE = 60 * 1000;
const ONE_HOUR = 60 * ONE_MINUTE;

const CONNECTION_THRESHOLD = ONE_MINUTE;

const states = {
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
};

class RealTimeConnection {
  // eslint-disable-next-line max-params
  constructor({ api, onConnect, onMessage, onDisconnect, lastActivity, pollingEnabled = true }) {
    this.api = api;
    this.pusherChannelName = `presence-external-${api.orgId}-tracking-${api.trackingId}`;
    this.onConnect = onConnect;
    this.onDisconnect = onDisconnect;
    this.onMessage = onMessage;
    this.status = states.DISCONNECTED;
    this.poller = null;
    this.millisSinceActivity = lastActivity || Date.now();
    this.skipFirstPoll = !!lastActivity;
    this.pollingEnabled = pollingEnabled;
  }

  start() {
    if (!this.pollingEnabled) {
      return this.updateConnectionState();
    }

    if (this.poller) return undefined;

    this.poller = new Poller({
      task: millisSinceLastPoll => this.updateConnectionState(millisSinceLastPoll),
      skipFirstPoll: this.skipFirstPoll,
    });

    return this.poller.start();
  }

  get connected() {
    return this.status === states.CONNECTED;
  }

  _connect() {
    this.status = states.CONNECTED;
    this.initPusher();
    this.joinChatSocketChannels().then(this.onConnect);
    if (this.poller) {
      this.poller.restart();
    }
  }

  _disconnect() {
    this.status = states.DISCONNECTED;
    this.disconnectPusher().then(this.onDisconnect);
    if (this.poller) {
      this.poller.restart();
    }
  }

  _toggleConnection(millisSinceLastPoll = 0) {
    if (!this.connected) {
      const threshold = Math.max(millisSinceLastPoll, CONNECTION_THRESHOLD);
      if (this.millisSinceActivity <= threshold || !this.pollingEnabled) this._connect();
    } else if (this.millisSinceActivity > CONNECTION_THRESHOLD && this.pollingEnabled) {
      this._disconnect();
    }
  }

  _updateMaxPollInterval() {
    if (!this.poller) return undefined;
    if (this.millisSinceActivity < ONE_HOUR) {
      return this.poller.setMaxInterval(0.5 * ONE_MINUTE);
    } else if (this.millisSinceActivity < 3 * ONE_HOUR) {
      return this.poller.setMaxInterval(5 * ONE_MINUTE);
    }
    return this.poller.setMaxInterval();
  }

  refresh() {
    if (!this.poller) return;
    this.poller.restart();
  }

  updateConnectionState(millisSinceLastPoll) {
    if (!this.pollingEnabled) {
      return this._toggleConnection();
    }

    return this.getCustomerStats().then(({ activity }) => {
      this.millisSinceActivity = getMillisSinceLastActivity(activity);
      this._updateMaxPollInterval();
      this._toggleConnection(millisSinceLastPoll);
    });
  }

  stop() {
    if (this.poller) {
      this.poller.stop();
    }
    this.disconnectPusher();
    this.poller = null;
  }

  getCustomerStats() {
    return this.api.getCustomerStats().then(data => {
      const activity = data.activity;

      return {
        activity: activity && this.api.convertServerToLocalDate(activity).getTime(),
      };
    });
  }

  initPusher() {
    Socket.init({
      pusherAuthEndpoint: `${this.api.orgBaseUrl}/c/v1/pusher/auth`,
      trackingToken: this.api.trackingToken,
      pusherAccessKey: this.api.pusherAccessKey,
      pusherCluster: this.api.pusherCluster,
    });
  }

  disconnectPusher() {
    return Socket.disconnect();
  }

  joinChatSocketChannels() {
    return Socket.joinAppChannel(this.pusherChannelName, this.onMessage);
  }

  leaveAndResetChatActivityChannel() {
    Socket.leaveChatActivityChannel();
    Socket.resetPusherActivityChannel();
  }

  joinChatActivityChannel(activeSessionId, onSessionMessage) {
    const chatActivityChannelName = `private-external-${this.api.orgId}-chat-activity-${activeSessionId}`;

    return Socket.joinChatActivityChannel(chatActivityChannelName, onSessionMessage);
  }

  leaveCustomerPresenceChannel() {
    Socket.leaveCustomerPresenceChannel();
    Socket.resetPusherPresenceChannel();
  }

  joinCustomerPresenceChannel(customerId, onSessionMessage) {
    const customerPresenceChannel = `presence-external-${this.api.orgId}-customer-activity-${customerId}`;

    return Socket.joinCustomerPresenceChannel(customerPresenceChannel, onSessionMessage);
  }

  sendChatActivity(activityData) {
    const { customerId, sessionId, status, eventName } = activityData;

    return Socket.sendChatActivity(
      {
        type: socketConstants.CONVERSATION_TYPE,
        id: sessionId,
        status,
        userId: customerId,
        userType: 'customer',
        createdAt: Date.now(),
      },
      eventName,
    );
  }
}

export default RealTimeConnection;
